'use client';

import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import * as React from 'react';
import DialogConfirm, {
    DialogConfirmProps,
    DialogConfirmRefs,
} from '../custom-dialog-confirm/dialog-confirm';
import CustomTooltip from '../custom-tooltip';
import { CustomTooltipProps } from '../custom-tooltip/tooltip';
import { Button, ButtonProps } from '../ui/button';

export interface ButtonCustomProps extends ButtonProps {
    isLoading?: boolean;
    icon?: React.ReactNode;
    iconPlacement?: 'left' | 'right';
    /**build-in tooltip */
    tooltipProps?: CustomTooltipProps & {
        hvWrapper?: boolean;
        wrapperClassName?: string;
    };

    /**build-in dialog-confirm */
    confirmDialogProps?: DialogConfirmProps;
}

const ButtonCustom = ({
    isLoading = false,
    icon,
    iconPlacement = 'left',
    tooltipProps,
    confirmDialogProps,
    children,
    ...rest
}: ButtonCustomProps) => {
    const _dialogConfirmRef = React.useRef<DialogConfirmRefs>(null);

    const onBtnClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (confirmDialogProps && _dialogConfirmRef.current) {
            _dialogConfirmRef.current.showModal();
        } else {
            rest.onClick?.(e);
        }
    };

    const _btn = (
        <Button
            {...rest}
            asChild={false}
            onClick={onBtnClick}
            disabled={rest.disabled || isLoading}
            className={cn('flex items-center gap-x-1.5', rest.className)}
        >
            {isLoading && (
                <div className='flex h-4 w-4 items-center justify-center'>
                    <Loader2 size={14} className='animate-spin' />
                </div>
            )}
            {!isLoading && icon && iconPlacement === 'left' && icon}
            {children}
            {!isLoading && icon && iconPlacement === 'right' && icon}
        </Button>
    );

    return (
        <>
            {tooltipProps ? (
                <CustomTooltip.Root {...tooltipProps}>
                    {tooltipProps.hvWrapper ? (
                        <div className={tooltipProps.wrapperClassName}>
                            {_btn}
                        </div>
                    ) : (
                        _btn
                    )}
                </CustomTooltip.Root>
            ) : (
                _btn
            )}

            {confirmDialogProps && (
                <DialogConfirm
                    ref={_dialogConfirmRef}
                    {...confirmDialogProps}
                />
            )}
        </>
    );
};

export default ButtonCustom;
